import React from 'react';
import { Rss } from 'lucide-react';

// Brand colors
const CYAN_PRIMARY = 'var(--pm-cyan)';
const CYAN_DIM = 'var(--pm-cyan-glow)';
const PODBEAN_GREEN = 'var(--pm-podbean-green)';
const PODBEAN_GREEN_DIM = 'var(--pm-podbean-green-dim)';

// Microphone SVG for Episode Composer
const MicIcon: React.FC = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="micGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor={CYAN_PRIMARY} />
        <stop offset="100%" stopColor='var(--pm-cyan-dim)' />
      </linearGradient>
    </defs>
    <path
      d="M12 1C10.34 1 9 2.34 9 4V12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12V4C15 2.34 13.66 1 12 1Z"
      fill="url(#micGradient)"
    />
    <path
      d="M17 12C17 14.76 14.76 17 12 17C9.24 17 7 14.76 7 12H5C5 15.53 7.61 18.43 11 18.92V22H13V18.92C16.39 18.43 19 15.53 19 12H17Z"
      fill="url(#micGradient)"
    />
  </svg>
);

// Podbean logo: RSS icon with pulse animation
const PodbeanLogo: React.FC = () => (
  <div style={{ animation: 'signalPulse 1.5s ease-in-out infinite' }}>
    <Rss size={36} color={PODBEAN_GREEN} strokeWidth={2.5} />
  </div>
);

const PublishingAnimation: React.FC = () => {
  return (
    <>
      {/* Keyframe animations */}
      <style>{`
        @keyframes packetMove {
          0% {
            left: 0;
            opacity: 0;
            background-color: ${CYAN_PRIMARY};
            box-shadow: 0 0 8px ${CYAN_DIM};
          }
          10% {
            opacity: 1;
          }
          50% {
            background-color: ${CYAN_PRIMARY};
            box-shadow: 0 0 8px ${CYAN_DIM};
          }
          90% {
            opacity: 1;
          }
          100% {
            left: calc(100% - 10px);
            opacity: 0;
            background-color: ${PODBEAN_GREEN};
            box-shadow: 0 0 8px ${PODBEAN_GREEN_DIM};
          }
        }

        @keyframes destinationPulse {
          0%, 100% {
            box-shadow: 0 0 15px ${PODBEAN_GREEN_DIM}, 0 0 30px rgba(139, 197, 63, 0.1);
          }
          50% {
            box-shadow: 0 0 25px ${PODBEAN_GREEN_DIM}, 0 0 50px rgba(139, 197, 63, 0.2);
          }
        }

        @keyframes sourcePulse {
          0%, 100% {
            box-shadow: 0 0 15px ${CYAN_DIM}, 0 0 30px rgba(0, 212, 255, 0.1);
          }
          50% {
            box-shadow: 0 0 20px ${CYAN_DIM}, 0 0 40px rgba(0, 212, 255, 0.15);
          }
        }

        @keyframes signalPulse {
          0%, 100% {
            opacity: 0.6;
            transform: scaleX(0.9);
          }
          50% {
            opacity: 1;
            transform: scaleX(1);
          }
        }
      `}</style>

      <div className="flex items-center justify-center gap-4 py-4">
        {/* Source: Episode Composer icon */}
        <div className="flex flex-col items-center gap-2">
          <div
            className="flex items-center justify-center rounded-xl"
            style={{
              width: '80px',
              height: '80px',
              backgroundColor: 'var(--pm-bg-base)',
              border: `2px solid ${CYAN_PRIMARY}`,
              animation: 'sourcePulse 2s ease-in-out infinite',
            }}
          >
            <MicIcon />
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            Episode Composer
          </span>
        </div>

        {/* Data stream with animated packets */}
        <div
          className="relative"
          style={{
            width: '120px',
            height: '20px',
          }}
        >
          {/* Background gradient line */}
          <div
            className="absolute top-1/2 left-0 right-0 -translate-y-1/2 rounded-full"
            style={{
              height: '4px',
              background: `linear-gradient(90deg, ${CYAN_PRIMARY} 0%, ${PODBEAN_GREEN} 100%)`,
              opacity: 0.3,
            }}
          />
          {/* Animated data packets */}
          {[0, 0.4, 0.8, 1.2].map((delay, index) => (
            <div
              key={index}
              className="absolute top-1/2 -translate-y-1/2 rounded-sm"
              style={{
                width: '10px',
                height: '10px',
                animation: `packetMove 1.6s ease-in-out infinite ${delay}s`,
              }}
            />
          ))}
        </div>

        {/* Destination: Podbean icon */}
        <div className="flex flex-col items-center gap-2">
          <div
            className="flex items-center justify-center rounded-xl"
            style={{
              width: '80px',
              height: '80px',
              backgroundColor: 'var(--pm-bg-base)',
              border: `2px solid ${PODBEAN_GREEN}`,
              animation: 'destinationPulse 2s ease-in-out infinite',
            }}
          >
            <PodbeanLogo />
          </div>
          <span className="text-xs text-muted-foreground">Podbean</span>
        </div>
      </div>
    </>
  );
};

export default PublishingAnimation;
